import { useState, useEffect } from "react";
import { api, money, formatApiError } from "../lib/api";
import { PageHeader, Card, Field, inputClass, PrimaryButton } from "../components/common";
import { toast } from "sonner";
import { FilePdf, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function printReport(d) {
  const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
  const range = `${d.start || "inicio"} a ${d.end || "hoy"}`;
  const pays = d.payments.map((p) => `<tr><td>${p.number}</td><td>${p.contract}</td><td>${p.contractor}</td><td>${p.period || "-"}</td><td>${p.paid_at}</td><td style="text-align:right">${fmt(p.net)}</td></tr>`).join("");
  const prog = d.contracts.map((c) => `<tr><td>${c.title}</td><td style="text-align:right">${c.progress}%</td></tr>`).join("");
  const montos = d.contracts.map((c) => `<tr><td>${c.title}</td><td style="text-align:right">${fmt(c.original_amount)}</td><td style="text-align:right">${(c.amount_delta > 0 ? "+" : "")}${fmt(c.amount_delta)}</td><td style="text-align:right">${fmt(c.amount)}</td></tr>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Reporte ejecutivo</title>
    <style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:32px;max-width:900px;margin:auto}
    h1{font-size:24px;margin:0}h2{font-size:15px;margin:24px 0 8px;border-bottom:2px solid #FF5A00;padding-bottom:4px}
    .sub{color:#64748b;font-size:13px}.kpis{display:flex;gap:16px;margin-top:16px}
    .kpi{flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:14px}.kpi b{display:block;font-size:22px;margin-top:4px}
    table{width:100%;border-collapse:collapse;font-size:13px;margin-top:6px}th,td{border-bottom:1px solid #e2e8f0;padding:7px 8px;text-align:left}
    th{color:#64748b;font-size:11px;text-transform:uppercase}tfoot td{font-weight:bold;border-top:2px solid #0f172a}
    </style></head><body>
    <div style="display:flex;align-items:center;gap:8px;color:#FF5A00;font-weight:800">Obra en Control</div>
    <h1>Reporte ejecutivo de obra</h1><div class="sub">Periodo: ${range} · Generado ${new Date().toLocaleString("es-MX")}</div>
    <div class="kpis">
      <div class="kpi">Pagos aplicados<b>${fmt(d.total_paid)}</b></div>
      <div class="kpi">Avance general<b>${d.overall_progress}%</b></div>
      <div class="kpi">Fotos en el periodo<b>${d.photos_count}</b></div>
    </div>
    <h2>Pagos aplicados en el periodo</h2>
    <table><thead><tr><th>#</th><th>Contrato</th><th>Contratista</th><th>Periodo</th><th>Pagado</th><th style="text-align:right">Neto</th></tr></thead>
    <tbody>${pays || '<tr><td colspan="6" style="color:#64748b">Sin pagos en el rango seleccionado.</td></tr>'}</tbody>
    <tfoot><tr><td colspan="5">Total</td><td style="text-align:right">${fmt(d.total_paid)}</td></tr></tfoot></table>
    <h2>Avance por frente de trabajo</h2>
    <table><thead><tr><th>Contrato</th><th style="text-align:right">Avance</th></tr></thead><tbody>${prog || '<tr><td colspan="2" style="color:#64748b">Sin contratos.</td></tr>'}</tbody></table>
    <h2>Montos por contrato (original vs adendums)</h2>
    <table><thead><tr><th>Contrato</th><th style="text-align:right">Original</th><th style="text-align:right">Ajustes</th><th style="text-align:right">Vigente</th></tr></thead>
    <tbody>${montos || '<tr><td colspan="4" style="color:#64748b">Sin contratos.</td></tr>'}</tbody>
    <tfoot><tr><td>Total</td><td style="text-align:right">${fmt(d.total_original)}</td><td style="text-align:right">${(d.total_amount_delta > 0 ? "+" : "")}${fmt(d.total_amount_delta)}</td><td style="text-align:right">${fmt(d.total_contracted)}</td></tr></tfoot></table>
    </body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
}

export default function Reportes() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [contractors, setContractors] = useState([]);
  const [data, setData] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/contractors").then((r) => setContractors(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/executive", { params: { start: start || undefined, end: end || undefined, contractor_id: contractorId || undefined } });
      setData(r.data);
      api.get("/analytics/planos-by-specialty").then((p) => setPlanos(p.data)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader overline="Documentos" title="Reportes PDF" />

      <Card className="p-6 mb-6">
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <Field label="Desde"><input type="date" data-testid="report-start" className={inputClass} value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="Hasta"><input type="date" data-testid="report-end" className={inputClass} value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          <Field label="Contratista">
            <select data-testid="report-contractor" className={inputClass} value={contractorId} onChange={(e) => setContractorId(e.target.value)}>
              <option value="">Todos</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <PrimaryButton onClick={generate} disabled={busy} data-testid="generate-report-btn"><ChartLineUp size={18} weight="bold" /> {busy ? "Generando…" : "Generar reporte"}</PrimaryButton>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Deja las fechas vacías para todo el histórico. Filtra por contratista para un reporte específico.</p>
      </Card>

      {data && (
        <div data-testid="report-result">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card className="p-5"><div className="text-sm text-muted-foreground">Pagos aplicados</div><div className="font-heading font-extrabold text-2xl text-primary mt-1">{money(data.total_paid)}</div></Card>
            <Card className="p-5"><div className="text-sm text-muted-foreground">Avance general</div><div className="font-heading font-extrabold text-2xl mt-1">{data.overall_progress}%</div></Card>
            <Card className="p-5"><div className="text-sm text-muted-foreground">Fotos en el periodo</div><div className="font-heading font-extrabold text-2xl mt-1">{data.photos_count}</div></Card>
          </div>

          <div className="flex justify-end mb-3">
            <PrimaryButton onClick={() => printReport(data)} data-testid="download-pdf-btn"><FilePdf size={18} weight="bold" /> Descargar PDF</PrimaryButton>
          </div>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["#", "Contrato", "Contratista", "Periodo", "Pagado", "Neto"].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}</tr></thead>
              <tbody>
                {data.payments.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Sin pagos en el rango seleccionado.</td></tr>
                ) : data.payments.map((p, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">{p.number}</td>
                    <td className="px-4 py-2.5">{p.contract}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{p.contractor}</td>
                    <td className="px-4 py-2.5">{p.period || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{p.paid_at}</td>
                    <td className="px-4 py-2.5 font-semibold text-primary">{money(p.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card className="overflow-x-auto mt-6" data-testid="report-montos">
            <div className="px-4 pt-4 overline">Montos por contrato (original vs adendums)</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Contrato", "Original", "Ajustes", "Vigente"].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}</tr></thead>
              <tbody>
                {data.contracts.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Sin contratos.</td></tr>
                ) : data.contracts.map((c, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">{c.title}</td>
                    <td className="px-4 py-2.5">{money(c.original_amount)}</td>
                    <td className={`px-4 py-2.5 font-semibold ${c.amount_delta > 0 ? "text-emerald-600" : c.amount_delta < 0 ? "text-red-600" : "text-muted-foreground"}`}>{c.amount_delta > 0 ? "+" : ""}{money(c.amount_delta)}</td>
                    <td className="px-4 py-2.5 font-semibold text-primary">{money(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {planos.length > 0 && (
        <Card className="p-6 mt-6" data-testid="planos-specialty-chart">
          <div className="overline mb-1">Avance de planos por especialidad</div>
          <p className="text-sm text-muted-foreground mb-4">Planos aprobados vs total por disciplina.</p>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={planos} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="specialty" angle={-30} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" name="Total" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                <Bar dataKey="approved" name="Aprobados" fill="#FF5A00" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
